import React, { ChangeEvent, Component, createRef, ReactNode } from 'react';
import { CaseAttachmentLog, CaseCommentLog } from '../../../../domain/case/CaseDomains';
import CaseService from '../../../../service/CaseService';
import { ServiceNames } from '../../../../constant/ServiceNames';
import ServiceContainer from '../../../../config/ServiceContainer';
import Icon from '../../../../icons/Icon';
import { RootState } from '../../../../redux/store';
import { addPagePath, changePagePath } from '../../../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../../helpers/withFormica';
import moment from 'moment/moment';
import { Loading } from '@trlogic/ui-pack';
import { TransactionData } from '../../../../domain/stream/transaction/TransactionDomains';
import { Mention } from 'primereact/mention';
import { FileUpload, FileUploadHandlerParam, ItemTemplateOptions } from 'primereact/fileupload';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import MessageUtil from '../../../../util/MessageUtil';
import CaseAttachmentItemComponent from './CaseAttachmentItemComponent';
import _ from 'lodash';
import ComponentName from '../../../../constant/ComponentName';
import LanguageUtil from '../../../../util/LanguageUtil';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import EnvironmentUtil from '../../../../util/EnvironmentUtil';
import { assertCanWriteChat, hasCasesChatWritePermission, notifyChatWriteNotPermitted } from '../../../../util/CaseBulkCloseUtil';

let messagesEndRef: any = null;

class CaseAttachmentChatComponent extends Component<Props, State> {
  public state: State = new State();

  private caseService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);
  private fileUploadRef = createRef<FileUpload>();
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private editInputRefs: { [key: string]: React.RefObject<HTMLInputElement> } = {};

  public render(): ReactNode {
    const { disabled } = this.props;
    const { comment, commentLoading, suggestions } = this.state;
    const writeDisabled: boolean = disabled || !hasCasesChatWritePermission();

    return (
      <div className={'w-full h-full px-5'} data-testid={`${ComponentName.CASE}_detail_attachment_modal_container`}>
        <div className={`flex-grow overflow-y-auto ${writeDisabled ? '' : 'pb-10'}`}>
          {this.getAttachments()}
          <div ref={(el) => (messagesEndRef = el)} />
        </div>
        {!writeDisabled && (
        <div className={'flex gap-2 p-2 absolute bottom-0 left-0 w-full items-center justify-center'}>
          <FileUpload
            auto
            ref={this.fileUploadRef}
            mode='basic'
            name='file'
            maxFileSize={this.environmentUtil.getStorageMaxSize()}
            headerTemplate={this.headerTemplate}
            itemTemplate={this.itemTemplateFile}
            customUpload
            disabled={writeDisabled}
            uploadHandler={this.uploadHandler}
            emptyTemplate={<p className='m-0'>{LanguageUtil.get({ id: 'common.label.dropFile', defaultMessage: 'Drag and drop files to here to upload.' })}</p>}
          />
          <Mention
            value={comment}
            onChange={this.onChangeComment}
            suggestions={suggestions}
            onSearch={this.onSearch}
            onKeyPress={this.handleKeyPress}
            delay={0}
            className={'w-full'}
            disabled={commentLoading || writeDisabled}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.mention', defaultMessage: 'Type @ to mention a user' })}
            rows={1}
            data-testid={`${ComponentName.CASE}_detail_attachment_comment_input`}
            itemTemplate={this.itemTemplate}
            autoResize
          />
          <div
            className={`${writeDisabled ? 'cursor-not-allowed' : 'cursor-pointer'} bg-white border rounded w-[34px] h-[34px] min-h-[34px] min-w-[34px] shrink-0 flex items-center justify-center align-center text-f-purple `}
            onClick={this.sendComment}
            data-testid={`${ComponentName.CASE}_detail_attachment_comment_send_button`}
          >
            {commentLoading ? <Loading /> : <Icon icon={'deploy'} size={20} color={'#284564'} />}
          </div>
        </div>
        )}
      </div>
    );
  }

  public componentDidMount(): void {
    this.scrollToBottom();
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (!_.isEqual(prevProps.attachments, this.props.attachments)) {
      this.scrollToBottom();
    }
    if (!_.isEqual(prevState.editComment, this.state.editComment)) {
      Object.keys(this.state.editComment).forEach((key) => {
        if (this.state.editComment[key] && this.editInputRefs[key]) {
          this.editInputRefs[key].current?.focus();
        }
      });
    }
  }

  private scrollToBottom = () => {
    messagesEndRef.scrollIntoView({ behavior: 'smooth' });
  };

  private getAttachments = () => {
    const { editComment } = this.state;
    const { attachments, transaction, user } = this.props;
    const canWriteChat: boolean = hasCasesChatWritePermission();

    return (
      attachments &&
      attachments.length > 0 &&
      attachments.map((attachment: any, index: number) => {
        if (attachment.type == 'COMMENT' || attachment.type == 'TAGGED_FIELD') {
          const isEditing = editComment[attachment.id];
          if (!this.editInputRefs[attachment.id]) {
            this.editInputRefs[attachment.id] = createRef();
          }

          return (
            <div
              key={attachment.id || attachment.timestamp}
              data-testid={`${ComponentName.CASE}_detail_attachment_comment_${index}_container`}
              className={`flex rounded-[10px] w-2/3 border ${transaction.id != attachment.transactionId ? 'bg-gray-100' : 'bg-white'} p-2 my-4 items-center gap-2 ${
                user.username === attachment.username ? 'ml-auto' : 'mr-auto'
              }`}
            >
              <div className={'flex justify-center align-center bg-[#D7E5FF] rounded-full w-[26px] h-[26px] min-h-[26px] min-w-[26px] shrink-0 '}>
                <div className={'text-f-purple'}>
                  <Icon icon={'people'} size={16} color={'white'} />
                </div>
              </div>
              <div className={'flex flex-col flex-grow'}>
                <div className={'flex justify-between'}>
                  <div className={'flex-grow text-xs text-[#6CA2FF]'} data-testid={`${ComponentName.CASE}_detail_attachment_comment_${index}_username`}>
                    {attachment.username}
                  </div>
                  <div className={'text-[#333333] text-2xs font-extralight'} data-testid={`${ComponentName.CASE}_detail_attachment_comment_${index}_date`}>
                    {moment(attachment.timestamp).format('LLLL').valueOf()}
                  </div>
                </div>
                <div className={'flex justify-between items-center gap-x-2'}>
                  <div
                    className={'flex-grow overflow-auto text-[#4F4F4F] text-md'}
                    data-testid={`${ComponentName.CASE}_detail_attachment_comment_${index}_content`}
                  >
                    {isEditing ? (
                      <input
                        defaultValue={attachment.content}
                        ref={this.editInputRefs[attachment.id]}
                        className={'w-full border border rounded px-1'}
                        onBlur={(event) => this.onBlurComment(event, attachment.id, attachment)}
                      />
                    ) : (
                      attachment.content
                    )}
                  </div>
                  <div>
                    {canWriteChat && (
                      <Icon icon={'edit'} size={16} color={'black'} className={'cursor-pointer'} onClick={() => this.editComment(attachment.id)} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        } else if (attachment.type == 'ATTACHMENT') {
          return (
            <div
              key={attachment?.id}
              className={`flex rounded-[10px] w-2/3 border ${transaction.id != attachment.transactionId ? 'bg-gray-100' : 'bg-white'} p-2 my-4 items-center gap-2 ${
                user.username === attachment.username ? 'ml-auto' : 'mr-auto'
              }`}
            >
              <div className={'flex justify-center align-center bg-[#D7E5FF] rounded-full w-[26px] h-[26px] min-h-[26px] min-w-[26px] shrink-0 '}>
                <div className={'text-f-purple'}>
                  <Icon icon={'file'} size={16} color={'white'} />
                </div>
              </div>
              <div className={'flex flex-col flex-grow'}>
                <div className={'flex justify-between'}>
                  <div className={'flex-grow text-xs text-[#6CA2FF]'}>
                    <span className={'text-[#6CA2FF] text-sm font-normal'} data-testid={`${ComponentName.CASE}_detail_attachment_comment_${index}_username`}>
                      {attachment.username}
                    </span>
                  </div>
                  <div className={'text-[#333333] text-2xs font-extralight'} data-testid={`${ComponentName.CASE}_detail_attachment_comment_${index}_date`}>
                    {moment(attachment.timestamp).format('LLLL').valueOf()}
                  </div>
                </div>
                <CaseAttachmentItemComponent key={attachment?.id} attachment={attachment as CaseAttachmentLog} />
              </div>
            </div>
          );
        }
      })
    );
  };

  private headerTemplate = (options: any) => {
    const { className, chooseButton, uploadButton, cancelButton } = options;
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center' }}>
        {chooseButton}
        {uploadButton}
        {cancelButton}
      </div>
    );
  };

  private itemTemplate = (suggestion: any) => {
    return (
      <div className='flex align-items-center p-4'>
        <span className='flex flex-column'>
          <small style={{ fontSize: '.75rem', color: 'var(--text-color-secondary)' }}>@{suggestion}</small>
        </span>
      </div>
    );
  };

  private onChangeComment = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ comment: event.target.value });
  };

  private handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      this.sendComment();
    }
  };

  private itemTemplateFile = (file: any, options: ItemTemplateOptions): React.ReactNode => {
    return (
      <div className='flex items-center flex-wrap'>
        <span className='flex text-left ml-3'>{file.name}</span>
        <small className='flex text-left mx-3'>{new Date().toLocaleDateString()}</small>
        <Tag value={options.formatSize} severity='warning' className='flex px-3 py-2' />
        <Button type='button' icon='pi pi-times' className='flex p-button-outlined p-button-rounded p-button-danger ml-auto' onClick={options.onRemove} />
      </div>
    );
  };

  private uploadHandler = async (e: FileUploadHandlerParam) => {
    const { domain, id, disabled } = this.props;
    if (!assertCanWriteChat() || domain == undefined || disabled) return;
    await this.caseService.attachment(domain, id, e.files[0]);
    MessageUtil.success({
      summary: 'File successfully uploaded!',
      detail: <div></div>,
    });
    if (this.fileUploadRef.current) {
      this.fileUploadRef.current.clear();
    }
  };

  private onBlurComment = async (event: ChangeEvent<HTMLInputElement>, id: string, attachment: any) => {
    const { domain } = this.props;
    if (!assertCanWriteChat() || domain == undefined) return;

    const oldContent = attachment.content;
    const newContent = event.target.value;
    attachment.content = newContent;

    try {
      await this.caseService.commentEditCase(attachment._index, newContent, id);
      this.setState((prevState) => ({
        editComment: {
          ...prevState.editComment,
          [id]: false,
        },
      }));
    } catch (e) {
      console.error(e);
      attachment.content = oldContent;
    }
  };

  private sendComment = async () => {
    const { comment } = this.state;
    const { id, domain, disabled } = this.props;
    if (disabled || !assertCanWriteChat()) return;
    if (comment == undefined || comment.trim().length == 0 || domain == undefined) return;
    this.setState({ commentLoading: true });
    try {
      await this.caseService.commentCase(domain, id, comment);
      this.setState({ comment: '' });
    } catch (e) {
      const isUnauthorizedResponse: boolean =
        typeof e === 'object' && e != null && 'response' in e &&
        (e as { response?: { status?: number } }).response?.status === 401;
      if (isUnauthorizedResponse) {
        notifyChatWriteNotPermitted();
      }
      console.error(e);
    } finally {
      this.setState({ commentLoading: false });
    }
  };

  private editComment = (id: string) => {
    if (!hasCasesChatWritePermission()) {
      return;
    }
    this.setState((prevState) => ({
      editComment: {
        ...prevState.editComment,
        [id]: !prevState.editComment[id],
      },
    }));
  };

  private onSearch = (event: any) => {
    const { transaction } = this.props;
    setTimeout(() => {
      const query = event.query;
      let suggestions;
      const variables = Object.entries(transaction.variables).map((key) => key[0]);
      if (!query.trim().length) {
        suggestions = [...variables];
      } else {
        suggestions = variables.filter((variable) => {
          return variable.toLowerCase().startsWith(query.toLowerCase());
        });
      }
      this.setState({ suggestions });
    }, 250);
  };
}

class State {
  comment: string = '';
  commentLoading: boolean = false;
  suggestions: any;
  editComment: { [key: string]: boolean } = {};
}

interface Props extends ReduxProps, FormicaProps {
  attachments: CaseCommentLog[] | CaseAttachmentLog[];
  id: string;
  domain: string | undefined;
  disabled: boolean;
  transaction: TransactionData;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
});
const mapDispatchToProps = { addPagePath, changePagePath };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(CaseAttachmentChatComponent));
